import { has } from '@lib/permission';
import { type IFolder } from '@store/api/folderEndpoints';

/** Id of the virtual root: the flat folder list carries it without an `_id`. */
export const ROOT_ID = 'root';

/** Characters a folder name cannot hold, the same set the API refuses. */
export const INVALID_FOLDER_NAME = /[\\/:*?"<>|]/;

/** A folder placed in the tree the reader can actually see. */
export interface FolderNode {
  id: string;
  folder: IFolder;
  parent: FolderNode | null;
  children: FolderNode[];
  isTeamRoot: boolean;
  /**
   * Lifted to the top level because its real parent is in a tree the reader holds only a
   * share in — the same folders the Folders page surfaces at its root.
   */
  isShared: boolean;
  projectCount: number;
}

export interface FolderTree {
  root: FolderNode;
  byId: Map<string, FolderNode>;
  /** Team names, read off the team root folders the reader can see. */
  teamNames: Map<string, string>;
}

/** What is being moved. `parentId` is where it sits now, `null` for the root. */
export interface MoveSubject {
  kind: 'Folder' | 'Project';
  id: string;
  name: string;
  parentId: string | null;
}

/**
 * Why a folder cannot take the subject. `current` is not a failure of the folder — it is
 * where the subject already is — so the dialog keeps it selectable and only disables Move.
 */
export type MoveBlock = 'self' | 'inside' | 'current' | 'permission' | 'nameTaken';

/** A parent reference arrives as a bare id or populated, depending on the endpoint. */
export const folderIdOf = (ref: string | { _id: string } | null | undefined): string | null =>
  !ref ? null : typeof ref === 'string' ? ref : ref._id;

/**
 * Builds the tree from `GET /v1/folders`, which lists every folder the reader may see with
 * its permissions, owner team and children — enough to answer every question the move
 * dialog asks without another request.
 */
export const buildFolderTree = (folders: IFolder[]): FolderTree => {
  const node = (folder: IFolder, id: string): FolderNode => ({
    id,
    folder,
    parent: null,
    children: [],
    isTeamRoot: Boolean(folder.teamId),
    isShared: false,
    projectCount: (folder.children ?? []).filter((child) => child.type === 'Project').length,
  });

  const rootFolder = folders.find((folder) => !folder._id)
    ?? { _id: '', name: '', path: '/', children: [], createdAt: '', updatedAt: '' };
  const root = node(rootFolder, ROOT_ID);
  const byId = new Map<string, FolderNode>([[ROOT_ID, root]]);

  folders.forEach((folder) => {
    if (folder._id) byId.set(folder._id, node(folder, folder._id));
  });

  byId.forEach((child) => {
    if (child === root) return;

    const parentId = folderIdOf(child.folder.parentFolder);
    const parent = (parentId && byId.get(parentId)) || root;

    child.isShared = Boolean(parentId) && parent === root;
    child.parent = parent;
    parent.children.push(child);
  });

  byId.forEach((parent) => parent.children.sort((a, b) => a.folder.name.localeCompare(b.folder.name)));

  const teamNames = new Map(folders
    .filter((folder) => folder.teamId)
    .map((folder) => [folder.teamId!, folder.name]));

  return { root, byId, teamNames };
};

/** The folders from below the root down to `node`, inclusive; empty for the root. */
export const trailOf = (node: FolderNode): FolderNode[] => {
  const trail: FolderNode[] = [];

  for (let current: FolderNode | null = node; current?.parent; current = current.parent) trail.unshift(current);

  return trail;
};

export const pathLabelOf = (node: FolderNode, rootLabel: string): string => {
  const trail = trailOf(node);

  return trail.length ? trail.map((n) => n.folder.name).join(' › ') : rootLabel;
};

/** True when `node` is the folder `ancestorId` or anywhere below it. */
export const isWithin = (node: FolderNode, ancestorId: string): boolean => {
  for (let current: FolderNode | null = node; current; current = current.parent) {
    if (current.id === ancestorId) return true;
  }

  return false;
};

/**
 * Mirrors the checks `updateFolder` and `patchProject` make on the destination, so a
 * folder the server would refuse is shown as such instead of failing after the click.
 */
export const moveBlockOf = (node: FolderNode, subject: MoveSubject): MoveBlock | null => {
  if (subject.kind === 'Folder') {
    if (node.id === subject.id) return 'self';
    if (isWithin(node, subject.id)) return 'inside';
  }

  if (node.id === (subject.parentId ?? ROOT_ID)) return 'current';

  // The container question, not `folder:put`: see `updateFolder` in the backend.
  if (!has(node.folder, subject.kind === 'Folder' ? 'folder:post' : 'project:post')) return 'permission';

  // The subject itself does not count: once a move lands, the refetched list shows it there.
  if (subject.kind === 'Folder'
    && node.children.some((child) => child.id !== subject.id && child.folder.name === subject.name)) {
    return 'nameTaken';
  }

  return null;
};

/**
 * Whether the reader could move the subject back to where it is now. False for a folder held
 * only through a share on itself: its parent is in a tree the reader cannot see, so once it
 * leaves, only an admin can put it back. Moving the subject never changes this answer — the
 * shares a move clears are the subject's own and those below it, never its parent's.
 */
export const canMoveBack = (tree: FolderTree, subject: MoveSubject): boolean => {
  const origin = tree.byId.get(subject.parentId ?? ROOT_ID);

  return origin ? has(origin.folder, subject.kind === 'Folder' ? 'folder:post' : 'project:post') : false;
};

/** Whether a new folder may be created in `node` from within the move dialog. */
export const canCreateIn = (node: FolderNode, subject: MoveSubject): boolean =>
  has(node.folder, 'folder:post') && !(subject.kind === 'Folder' && isWithin(node, subject.id));

/** The team that owns the subject now: a folder's own, a project's through its folder. */
export const ownerTeamOf = (tree: FolderTree, subject: MoveSubject): string | undefined =>
  (subject.kind === 'Folder'
    ? tree.byId.get(subject.id)
    : tree.byId.get(subject.parentId ?? ROOT_ID))?.folder.ownerTeamId;

/**
 * Shares the backend clears when a folder lands under another team: those on the folder
 * and on every folder below it.
 */
export const sharesWithin = (node: FolderNode | undefined): number =>
  !node ? 0 : (node.folder.sharedWith?.length ?? 0)
    + node.children.reduce((sum, child) => sum + sharesWithin(child), 0);
